import { FocusContext, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import PersonalAssistantLogo from '../../PersonalAssistantLogo';
import { AppDispatch, useSelector } from '../../../../redux/store';
import { PizzaSubCategoryCard } from './PizzaSubCategoryCard';
import { useEffect, useState } from 'react';
import StepBackButton from '../StepBackButton';
import { useDispatch } from 'react-redux';
import {
    ResetChosenCategory,
    UpdateChooseSubCategoryQuery,
    UpdateChosenSubCategory,
} from '../../../../redux/slices/personalAssistantSlice';
import useSpeechRecognition from '../../../../components/SpeechToText/useSpeechRecognition';
import SpeechToTextButton from '../../../../components/SpeechToText/SpeechToTextButton';
import useMelingoQuery from '../useMelingoQuery';
import { findWhatIsTheCategory } from '../../PersonalAssistantHelperFunctions';
import createTranslateFunction from '../../../../i18n/createTranslateFunction';

type ChoosePizzaSubCategoryProps = {
    focusKey: string;
    onRowFocus: () => void;
    setChooseCategoryKey: React.Dispatch<React.SetStateAction<number>>;
    setChooseSubCategoryKey: React.Dispatch<React.SetStateAction<number>>;
    handleErrorMessageFromMelingo: (errorMssage?: string) => void;
    isDisabled: boolean;
};
const ChoosePizzaSubCategory = ({
    focusKey: focusKeyParam,
    onRowFocus,
    setChooseCategoryKey,
    setChooseSubCategoryKey,
    handleErrorMessageFromMelingo,
    isDisabled,
}: ChoosePizzaSubCategoryProps) => {
    const {
        isListening,
        startListening,
        stopListening,
        voiceRecognitionText,
        setVoiceRecognitionText,
    } = useSpeechRecognition();
    const { isMelingoLoading, melingoData, melingoError, fetchMelingoData } = useMelingoQuery();
    const [chosenSubCategoryUserInput, setChosenSubCategoryUserInput] = useState<string>('');
    const ChosenCategory = useSelector((state) => state.personalAssistant.chosenCategory);
    const { assistantStep, chosenSubCategoryQuery } = useSelector(
        (state) => state.personalAssistant,
    );
    const pizzaSubCategoryTranslation = createTranslateFunction(
        'personal_assistant_screen.choose_pizza_sub_category',
    );
    const menuCategories = useSelector((state) => state.dbFoodMenu.categories);
    const isCurrentStep = assistantStep === 'choosePizzaSubCategory' ? true : false;
    const dispatch = useDispatch<AppDispatch>();
    const [currentFocusIndex, setCurrentFocusIndex] = useState<number>(0);
    const { ref, focusKey, focusSelf } = useFocusable({
        focusable: true,
        onFocus: onRowFocus,
        focusKey: focusKeyParam,
        isFocusBoundary: true,
        focusBoundaryDirections: ['up'],
        preferredChildFocusKey: `STT-CHOOSE-PIZZA-SUBCATEGORY`,
    });

    const handleStepBackButton = () => {
        setChooseCategoryKey((prev) => prev + 1);
        dispatch(ResetChosenCategory());
    };

    const handleReceivedSubCategoryDataFromMelingo = () => {
        if (
            !melingoData ||
            !melingoData.data ||
            !melingoData.data.reply ||
            !melingoData.data.reply.items ||
            melingoError
        ) {
            console.log('No data received from Melingo');
            handleErrorMessageFromMelingo(`קרתה בעיה, בואו ננסה שוב`);
            dispatch(UpdateChooseSubCategoryQuery(''));
            setChooseSubCategoryKey((prev) => prev + 1);
            return;
        }
        const melingoAnswerType = melingoData.data.reply.items[0].pizzacategory;
        const pizzaSubCategories = menuCategories[0].sub_categories;
        const whatIsTheCategory = findWhatIsTheCategory(pizzaSubCategories, melingoAnswerType);

        if (whatIsTheCategory) {
            dispatch(UpdateChosenSubCategory(whatIsTheCategory));
        } else {
            handleErrorMessageFromMelingo(
                `לא מצאנו סוג הפיצה  "${chosenSubCategoryQuery}", בואו ננסה שוב`,
            );
            dispatch(UpdateChooseSubCategoryQuery(''));
            setChooseSubCategoryKey((prev) => prev + 1);
        }
    };

    useEffect(() => {
        onRowFocus();
        focusSelf();
    }, []);

    // Debounce timeout
    const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (voiceRecognitionText.length > 0) {
            // Clear any existing timeout
            if (debounceTimeout) {
                clearTimeout(debounceTimeout);
            }

            // Set a new timeout to process the voice input after a delay
            const timeout = setTimeout(() => {
                console.log('Processing voice input');
                dispatch(UpdateChooseSubCategoryQuery(voiceRecognitionText));
                console.log('Sending to Melingo ', voiceRecognitionText);
                fetchMelingoData(voiceRecognitionText);
                setVoiceRecognitionText(''); // Reset the text after processing
            }, 1000); // Adjust the delay as needed (1000 ms = 1 second)

            setDebounceTimeout(timeout);
        }

        // Cleanup function to clear the timeout if the component unmounts
        return () => {
            if (debounceTimeout) {
                clearTimeout(debounceTimeout);
            }
        };
    }, [voiceRecognitionText]);

    // useEffect(() => {
    //     if (voiceRecognitionText.length > 0) {
    //         dispatch(UpdateChooseSubCategoryQuery(voiceRecognitionText));
    //         fetchMelingoData(voiceRecognitionText);
    //     }
    //     //Reset the current value, so it wont call melingo again.
    // }, [voiceRecognitionText]);

    useEffect(() => {
        if (melingoData && melingoData.data) {
            setVoiceRecognitionText('');
            handleReceivedSubCategoryDataFromMelingo();
        }
    }, [melingoData]);

    return (
        <FocusContext.Provider value={focusKey}>
            <div
                className={`flex flex-col items-start justify-center animate-slowFadeIn w-full pr-[4vw] my-[2vw] ${isDisabled && `pointer-events-none`}`}
            >
                <div className="flex relative">
                    <span className="text-[1.8vw]">{pizzaSubCategoryTranslation('header')}</span>
                    <PersonalAssistantLogo />
                </div>
                <div ref={ref} className="flex">
                    <div className="flex items-start mt-[3vw] ml-[2vw]">
                        {isCurrentStep && (
                            <StepBackButton
                                focusKey="SBB-CHOOSE-PIZZA-SUBCATEGORY"
                                handleOnClick={handleStepBackButton}
                                className="ml-[1.5vw]"
                            />
                        )}
                        <SpeechToTextButton
                            focusKey="STT-CHOOSE-PIZZA-SUBCATEGORY"
                            isListening={isListening}
                            startClickFunction={startListening}
                            stopClickFunction={stopListening}
                        />
                    </div>
                    <div className="flex flex-wrap items-center justify-start w-[70vw] px-[0.5vw]">
                        {ChosenCategory &&
                            ChosenCategory.sub_categories.map((category, index) => {
                                return (
                                    <PizzaSubCategoryCard
                                        subCategoryData={category}
                                        setCurrentFocusIndex={setCurrentFocusIndex}
                                        componentIndex={index}
                                        key={`sub-category-card-${index}`}
                                    />
                                );
                            })}
                    </div>
                </div>
            </div>
        </FocusContext.Provider>
    );
};

export default ChoosePizzaSubCategory;
